const toFiniteNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export function getCommissionRange(data = {}) {
  const min = toFiniteNumber(data.min_grievance_percent);
  const max = toFiniteNumber(data.max_grievance_percent);

  if (min === null || max === null || min > max) return null;

  return { min, max, isFixed: min === max };
}

export function shouldUseAbsoluteCommission(subdomain, sliderData) {
  const range = getCommissionRange(sliderData);

  return subdomain === 'kavak' && Boolean(range) && (sliderData?.grievance_range_enabled === true || range.isFixed);
}

export function clampCommission(value, min, max) {
  return Math.min(Math.max(Number(value), min), max);
}

export function getInitialCommissionValue(sliderData, currentCommission, useAbsoluteCommission) {
  if (!useAbsoluteCommission) {
    const maxGrievancePercent = Number(sliderData?.grievance_percent || 0);
    const commission = toFiniteNumber(currentCommission);

    if (commission !== null && maxGrievancePercent) {
      return clampCommission((commission * 100) / maxGrievancePercent, 0, 100);
    }

    return sliderData?.start_slider === 'max' ? 100 : 0;
  }

  const range = getCommissionRange(sliderData);
  if (!range) return 0;

  const commission = toFiniteNumber(currentCommission);
  if (commission !== null) return clampCommission(commission, range.min, range.max);

  return sliderData?.start_slider === 'max' ? range.max : range.min;
}

export function getCommissionMarks(min, max) {
  if (min === max) return [{ value: min, label: `${min}%` }];

  const marks = [
    { value: min, label: `${min}%` },
    { value: max, label: `${max}%` },
  ];

  if (min < 0 && max > 0) marks.splice(1, 0, { value: 0, label: '0%' });

  return marks;
}

export function getCommissionSliderConfig({
  grievancePercent,
  minGrievancePercent,
  maxGrievancePercent,
  percentCommission,
  useAbsoluteCommission,
}) {
  if (!useAbsoluteCommission) {
    const max = Number(grievancePercent ?? 0);

    return {
      min: 0,
      max: 100,
      value: percentCommission,
      marks: [
        { value: 0, label: '0%' },
        { value: 100, label: `${max}%` },
      ],
      isFixed: false,
      shouldShow: max !== 0,
      step: 1,
    };
  }

  const range = getCommissionRange({
    min_grievance_percent: minGrievancePercent,
    max_grievance_percent: maxGrievancePercent,
  });
  if (!range) return { shouldShow: false, isFixed: false };

  const value = toFiniteNumber(percentCommission);

  return {
    ...range,
    value: clampCommission(value ?? range.min, range.min, range.max),
    marks: getCommissionMarks(range.min, range.max),
    shouldShow: true,
    step: 1,
  };
}

export function getCommissionPayloadValue({
  percentCommission,
  grievancePercent,
  minGrievancePercent,
  maxGrievancePercent,
  useAbsoluteCommission,
}) {
  if (!useAbsoluteCommission) {
    return percentCommission > 0 ? (percentCommission * grievancePercent) / 100 : 0;
  }

  const range = getCommissionRange({
    min_grievance_percent: minGrievancePercent,
    max_grievance_percent: maxGrievancePercent,
  });
  const commission = toFiniteNumber(percentCommission) ?? 0;

  return range ? clampCommission(commission, range.min, range.max) : commission;
}

export function shouldDisplayCommissionInSidebar(subdomain) {
  return subdomain !== 'kavak';
}

export function getSidebarCommissionValue(quotationResponse, fallbackValue = 0) {
  const value = quotationResponse?.premiumSummary?.commission ?? quotationResponse?.commissionValue ?? fallbackValue;
  const commission = Number(value);

  return Number.isFinite(commission) ? commission : 0;
}

const getErrorMessage = (errorData) => {
  if (typeof errorData === 'string') return errorData;
  if (typeof errorData?.error === 'string') return errorData.error;
  if (typeof errorData?.message === 'string') return errorData.message;
  return '';
};

const getStructuredErrorRange = (errorData) => {
  if (!errorData || typeof errorData === 'string') return null;

  const candidates = [
    errorData,
    errorData.range,
    errorData.limits,
    errorData.grievance_range,
    typeof errorData.error === 'object' ? errorData.error : null,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const min = toFiniteNumber(candidate.min_grievance_percent ?? candidate.min);
    const max = toFiniteNumber(candidate.max_grievance_percent ?? candidate.max);

    if (min !== null || max !== null) return { min, max };
  }

  return null;
};

const getLegacyErrorRange = (errorMessage) => {
  if (!errorMessage) return null;

  const numberPattern = '(-?\\d+(?:[.,]\\d+)?)';
  const rangeMatch = errorMessage.match(new RegExp(`entre\\s*${numberPattern}%?\\s*e\\s*${numberPattern}%`, 'i'));
  if (rangeMatch) {
    return {
      min: Number(rangeMatch[1].replace(',', '.')),
      max: Number(rangeMatch[2].replace(',', '.')),
    };
  }

  const fixedMatch = errorMessage.match(new RegExp(`deve\\s+ser\\s*${numberPattern}%`, 'i'));
  if (fixedMatch) {
    const value = Number(fixedMatch[1].replace(',', '.'));
    return { min: value, max: value };
  }

  const limitMatch = errorMessage.match(new RegExp(`Limite:\\s*${numberPattern}%`, 'i'));
  if (!limitMatch) return null;

  return { min: null, max: Number(limitMatch[1].replace(',', '.')) };
};

const clampToPartialRange = (value, range) => {
  if (range.min !== null && value < range.min) return range.min;
  if (range.max !== null && value > range.max) return range.max;
  return value;
};

export function getCommissionLimitAdjustment({ errorData, currentCommission, grievancePercent, useAbsoluteCommission }) {
  const range = getStructuredErrorRange(errorData) || getLegacyErrorRange(getErrorMessage(errorData));
  if (!range) return null;
  if (range.min !== null && range.max !== null && range.min > range.max) return null;

  const current = toFiniteNumber(currentCommission);
  if (current === null) return null;

  const adjustedCommission = clampToPartialRange(current, range);

  if (useAbsoluteCommission) {
    if (adjustedCommission === current) return null;
    return { sliderValue: adjustedCommission, commissionPercent: adjustedCommission };
  }

  const relativeMax = toFiniteNumber(grievancePercent);
  if (relativeMax === null || relativeMax <= 0) return null;

  const currentActualCommission = current > 0 ? (current * relativeMax) / 100 : 0;
  const adjustedActualCommission = clampToPartialRange(currentActualCommission, range);
  const sliderValue = clampCommission((adjustedActualCommission * 100) / relativeMax, 0, 100);
  if (sliderValue === current) return null;

  return {
    sliderValue,
    commissionPercent: adjustedActualCommission,
  };
}
